import React, { useEffect, useMemo, useState } from "react";
import {
  Drawer,
  List,
  Divider,
  Typography,
  Button,
  Modal,
  Upload,
  Checkbox,
  Space,
  message,
} from "antd";
import { getCustomersApi, getMyTabAccessApi } from "../apiservice/setting";
import { useDispatch, useSelector } from "react-redux";
import {
  ClockCircleOutlined,
  UserAddOutlined,
  BellOutlined,
  PlusCircleOutlined,
  EditOutlined,
  DeleteOutlined,
  FileTextOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "./rightSidebar.css";

dayjs.extend(relativeTime);

// activity.type is a short code written at log time: c|u|d (create/update/
// delete). Older/legacy rows and a few non-CRUD writers (e.g. workflow
// actions) don't always set it, so this falls back to a neutral icon rather
// than guessing.
const ACTIVITY_TYPE_STYLE = {
  c: { icon: PlusCircleOutlined, color: "#52c41a", bg: "#f6ffed" },
  u: { icon: EditOutlined, color: "#1677ff", bg: "#e6f4ff" },
  d: { icon: DeleteOutlined, color: "#ff4d4f", bg: "#fff1f0" },
};

const getActivityStyle = (type) => ACTIVITY_TYPE_STYLE[type] || {
  icon: FileTextOutlined,
  color: "#8c8c8c",
  bg: "#fafafa",
};

// Relative time ("2 hours ago") reads naturally for anything recent; beyond a
// week it's more useful as an actual date than "2 weeks ago".
const formatActivityTime = (date) => {
  const d = dayjs(date);
  if (!d.isValid()) return "";
  return Date.now() - d.valueOf() < 7 * 86400000 ? d.fromNow() : d.format("DD MMM YYYY, HH:mm");
};
import { getSuppliersApi } from "../apiservice/purchase";
import {
  listMyTasks,
  approveTask,
  rejectTask,
  updateTaskProgress,
} from "../apiservice/workflow";
import { Link, useNavigate } from "react-router-dom";
import { loadAllNotifications } from "../store/commonThunks/notificationsThunk";
import {
  listNotifications,
  markNotificationRead,
  markAllNotificationsRead,
} from "../apiservice/workflow";
import { setNotificationCount } from "../store/commonSlices/notificationSlice";
import Loading from "./Loading";
import { hasWorkflowAccess } from "../lib/workflowAccess";
import { selectAuthInfo } from "../pages/Auth/authSelectors";

const { Text, Title } = Typography;
const { Dragger } = Upload;

// Max rows shown per section in the drawer.
const MAX_ITEMS = 10;

// Identity of each notification, used to remember what "Clear all" dismissed.
// Comparing against these keys (not timestamps) keeps it independent of any
// clock/timezone difference between the server and the browser. Expiries
// include the day count, so a cleared reminder comes back the next day.
const NOTIFICATION_KEYS = {
  expiries: (e) => `${e.type}|${e.employee_name}|${e.label}|${e.days}`,
  workflowNotifs: (n) => String(n.id),
  activities: (a) => String(a.activity_id),
  attendance: (a) => `${a.employee_name}|${a.date}|${a.type}|${a.created_date}`,
  customers: (c) => String(c.acc_id),
  suppliers: (s) => String(s.acc_id),
};
const EMPTY_CLEARED = Object.fromEntries(Object.keys(NOTIFICATION_KEYS).map((k) => [k, []]));

const RightSidebar = ({ visible, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authInfo = useSelector(selectAuthInfo);

  const canUseWorkflows = hasWorkflowAccess(authInfo);

  // Granted tabs for the user's actual role(s) (role_access.tab), fetched
  // once per drawer session. This reflects real permissions rather than
  // guessing from the role's display name, so it works for any role —
  // including custom ones created after the fact.
  const [myTabs, setMyTabs] = useState(null);
  const hasTab = (tab) => Array.isArray(myTabs) && myTabs.includes(tab);

  const isAdmin = hasTab("admin-dashboard");
  const canSeeExpiries = isAdmin || hasTab("hrpayroll");
  const canSeeCrmNotifications = isAdmin || hasTab("crm") || hasTab("sales");
  const canSeeCustomersSuppliers = isAdmin || hasTab("sales") || hasTab("purchase");

  // ⬇️ FETCH FROM REDUX (clean!)
  const activities = useSelector((s) => s.notifications.activityListAll);
  const expiries = useSelector((s) => s.notifications.expiryList);  
  const crm = useSelector((s) => s.crmNotifications.data);
  const attendanceListAll = useSelector(
    (s) => s.notifications.attendanceListAll
  );

  // What the user dismissed with "Clear all": per section, the keys of every
  // notification that was loaded at that moment.
  const clearedStorageKey = `notifications_cleared_v2_${authInfo?.user_id || authInfo?.id || "user"}`;
  const [cleared, setCleared] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(clearedStorageKey)) || {};
      return Object.fromEntries(
        Object.keys(NOTIFICATION_KEYS).map((k) => [k, Array.isArray(saved[k]) ? saved[k] : []])
      );
    } catch (err) {
      return EMPTY_CLEARED;
    }
  });

  const [workflowTasks, setWorkflowTasks] = useState([]);
  const [workflowNotifs, setWorkflowNotifs] = useState([]);
  const [actionLoading, setActionLoading] = useState({
    approve: null,
    reject: null,
    complete: null,
  });
  const [taskForm, setTaskForm] = useState(null);
  const [taskChecklist, setTaskChecklist] = useState([]);
  const [taskFileList, setTaskFileList] = useState([]);

  useEffect(() => {
    let intervalId;

    if (visible) {
      loadData();
      intervalId = setInterval(loadData, 7200000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [visible]);

  const loadData = async () => {
    setLoading(true);

    if (canUseWorkflows) {
      // Fetched separately so legacy calls below cannot blank out this section.
      listNotifications(false)
        .then((r) => setWorkflowNotifs(r?.data?.data || r?.data || []))
        .catch((e) => console.error("workflow notifications fetch failed:", e));
    }

    try {
      // Expiries, activities, attendance and the badge count all come from this thunk.
      dispatch(loadAllNotifications({ onlyIncludeHr: false }));

      const [cust, supp, wfTasks, tabAccess] = await Promise.all([
        getCustomersApi().catch(() => null),
        getSuppliersApi().catch(() => null),
        canUseWorkflows ? listMyTasks("Pending").catch(() => null) : Promise.resolve(null),
        getMyTabAccessApi().catch(() => null),
      ]);

      // Only the latest few are ever rendered, so don't keep the full lists in state.
      setCustomers((cust?.data || []).slice(0, MAX_ITEMS));
      setSuppliers((supp?.data || []).slice(0, MAX_ITEMS));
      setWorkflowTasks(wfTasks?.data?.data || wfTasks?.data || []);
      setMyTabs(tabAccess?.data?.tabs || []);
    } catch (err) {
      console.error("RightSidebar fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const reloadWorkflowTasks = async () => {
    if (!canUseWorkflows) return;

    try {
      const res = await listMyTasks("Pending");
      setWorkflowTasks(res?.data?.data || res?.data || []);
    } catch (err) {
      console.error("Failed to reload workflow tasks", err);
    }
  };

  // Click a workflow notification → mark read, close the drawer, follow its link.
  const handleWorkflowNotifClick = async (notif) => {
    try {
      if (notif?.id && !notif.read_at) await markNotificationRead(notif.id);
      setWorkflowNotifs((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, read_at: new Date().toISOString() } : n))
      );
    } catch (err) {
      // non-fatal — still navigate
    }
    onClose();
    if (notif?.link) navigate(notif.link);
  };

  const getRecordEditPath = (recordType, recordId, workflowName) => {
    switch (recordType) {
      case "Job":
        return `/ats/jobs/${recordId}/edit?workflow=${workflowName}`;
      case "Candidate":
        return `/ats/candidates/${recordId}/edit?workflow=${workflowName}`;
      default:
        return null;
    }
  };

  const handleApprove = async (taskId) => {
    try {
      setActionLoading({ approve: taskId, reject: null });
      await approveTask(taskId);
      message.success("Task approved");
      reloadWorkflowTasks(); // 🔄 reload list
    } catch (err) {
      message.error("Failed to approve task");
    } finally {
      setActionLoading({ approve: null, reject: null });
    }
  };

  const handleReject = async (taskId) => {
    try {
      setActionLoading({ reject: taskId, approve: null });
      await rejectTask(taskId);
      message.success("Task rejected");
      reloadWorkflowTasks(); // 🔄 reload list
    } catch (err) {
      message.error("Failed to reject task");
    } finally {
      setActionLoading({ approve: null, reject: null, complete: null });
    }
  };

  const openTaskForm = (task) => {
    const progress = task.task_progress || {};
    const preChecklist = Array.isArray(progress.checklist) ? progress.checklist : [];
    const preFiles = Array.isArray(progress.files) ? progress.files : [];
    setTaskChecklist(preChecklist);
    setTaskFileList(
      preFiles.map((name, idx) => ({
        uid: `existing-${idx}-${name}`,
        name,
        status: "done",
      }))
    );
    setTaskForm(task);
  };

  const submitTaskForm = async () => {
    if (!taskForm) return;
    try {
      setActionLoading({ approve: null, reject: null, complete: taskForm.id });
      const files = (taskFileList || []).map((f) => f.name).filter(Boolean);
      const res = await updateTaskProgress(taskForm.id, { checklist: taskChecklist, files });
      if (res?.error) throw new Error(res?.message || "Task update failed");
      if (res?.data?.auto_completed || res?.auto_completed) {
        message.success("Task completed");
        setTaskForm(null);
      } else {
        message.info("Progress saved. Complete required checklist/file items to finish.");
      }
      reloadWorkflowTasks();
    } catch (err) {
      message.error(err?.message || "Failed to update task progress");
    } finally {
      setActionLoading({ approve: null, reject: null, complete: null });
    }
  };


  // Drawer shows the latest 10 notifications in total, across every section the
  // user is allowed to see; "View All" / "My approvals" open the full lists.
  const latest = useMemo(() => {
    const toTime = (value) => {
      const t = new Date(value).getTime();
      return Number.isNaN(t) ? 0 : t;
    };
    const now = Date.now();
    // Anything dismissed by the last "Clear all" stays hidden.
    const tag = (kind, list, getTime) => {
      const keyOf = NOTIFICATION_KEYS[kind];
      const hidden = keyOf ? new Set(cleared[kind]) : null;
      return list
        .filter((item) => !hidden || !hidden.has(keyOf(item)))
        .map((item) => ({ kind, item, time: getTime(item) }));
    };

    const merged = [
      // Expiries have no created date — rank them by how soon they are due,
      // so one expiring today counts as "now".
      ...tag("expiries", canSeeExpiries ? expiries : [], (e) => now - e.days * 86400000),
      ...tag("workflowNotifs", workflowNotifs, (n) => toTime(n.created_at)),
      // Pending tasks are to-dos, not notifications — "Clear all" never hides them.
      ...tag("workflowTasks", workflowTasks, (t) => toTime(t.created_at)),
      ...tag("activities", activities, (a) => toTime(a.created_date)),
      ...tag("attendance", attendanceListAll, (a) => toTime(a.created_date || a.date)),
      ...tag("customers", canSeeCustomersSuppliers ? customers : [], (c) => toTime(c.created_date)),
      ...tag("suppliers", canSeeCustomersSuppliers ? suppliers : [], (s) => toTime(s.created_date)),
    ]
      .sort((a, b) => b.time - a.time)
      .slice(0, MAX_ITEMS);

    const bySection = {
      expiries: [],
      workflowNotifs: [],
      workflowTasks: [],
      activities: [],
      attendance: [],
      customers: [],
      suppliers: [],
    };
    merged.forEach(({ kind, item }) => bySection[kind].push(item));
    return bySection;
  }, [
    expiries,
    workflowNotifs,
    workflowTasks,
    activities,
    attendanceListAll,
    customers,
    suppliers,
    canSeeExpiries,
    canSeeCustomersSuppliers,
    cleared,
  ]);

  const recentExpiries = latest.expiries;
  const recentWorkflowNotifs = latest.workflowNotifs;
  const recentWorkflowTasks = latest.workflowTasks;
  const recentActivities = latest.activities;
  const recentAttendance = latest.attendance;
  const recentCustomers = latest.customers;
  const recentSuppliers = latest.suppliers;
  const unreadWorkflowCount = workflowNotifs.filter((n) => !n.read_at).length;

  const hasClearable =
    recentExpiries.length > 0 ||
    recentWorkflowNotifs.length > 0 ||
    recentActivities.length > 0 ||
    recentAttendance.length > 0 ||
    recentCustomers.length > 0 ||
    recentSuppliers.length > 0;

  // Hides everything currently loaded (remembered per user in this browser) —
  // anything that arrives afterwards still shows — and marks workflow
  // notifications as read on the server.
  const handleClearAll = () => {
    const loaded = { expiries, workflowNotifs, activities, attendance: attendanceListAll, customers, suppliers };
    const next = Object.fromEntries(
      Object.entries(NOTIFICATION_KEYS).map(([kind, keyOf]) => [kind, (loaded[kind] || []).map(keyOf)])
    );
    setCleared(next);
    try {
      localStorage.setItem(clearedStorageKey, JSON.stringify(next));
    } catch (err) {
      // storage unavailable — still cleared for this session
    }
    dispatch(setNotificationCount(0));

    if (canUseWorkflows && unreadWorkflowCount > 0) {
      const readAt = new Date().toISOString();
      markAllNotificationsRead()
        .then(() =>
          setWorkflowNotifs((prev) => prev.map((n) => (n.read_at ? n : { ...n, read_at: readAt })))
        )
        .catch((e) => console.error("mark all workflow notifications read failed:", e));
    }
  };

  return (
    <Drawer
      title={<Title level={4}>🔔 Notifications</Title>}
      extra={
        <Button size="small" disabled={loading || !hasClearable} onClick={handleClearAll}>
          Clear all
        </Button>
      }
      placement="right"
      width={380}
      onClose={onClose}
      open={visible}
      className="rightSidebar"
      styles={{
        body: {
          padding: "16px",
          display: "flex",
          flexDirection: "column",
          height: "100%",
        },
      }}
    >
      {loading ? (
        <Loading size="large" style={{ margin: "auto" }} />
      ) : (
        <div style={{ overflowY: "auto", flex: 1 }}>
          {/* Expiries — HR/company-sensitive, admins and HR only */}
          {recentExpiries.length > 0 && (
            <>
              <Divider orientation="left">
                <ClockCircleOutlined /> Expiries
              </Divider>

              <List
                dataSource={recentExpiries}
                renderItem={(item, i) => (
                  <List.Item key={i}>
                    <div>
                      <Text strong>{item.employee_name}</Text>
                      <br />
                      <Text type="secondary">
                        {item.label}: {item.days} days
                      </Text>
                    </div>
                  </List.Item>
                )}
              />
            </>
          )}

          {canUseWorkflows && recentWorkflowNotifs.length > 0 && (
            <>
              {/* Workflow notifications (triggered / task assigned) */}
              <Divider orientation="left">
                <BellOutlined /> Workflow
                {unreadWorkflowCount > 0 && (
                  <span style={{ marginLeft: 8, background: "#1677ff", color: "#fff", padding: "1px 8px", borderRadius: 10, fontSize: 12 }}>
                    {unreadWorkflowCount}
                  </span>
                )}
              </Divider>

              <List
                  dataSource={recentWorkflowNotifs}
                  renderItem={(n) => (
                    <List.Item
                      key={n.id}
                      onClick={() => handleWorkflowNotifClick(n)}
                      style={{
                        cursor: n.link ? "pointer" : "default",
                        background: n.read_at ? "transparent" : "#f0f7ff",
                        borderRadius: 6,
                        padding: "8px 10px",
                      }}
                    >
                      <div style={{ width: "100%" }}>
                        <Text strong>{n.title}</Text>
                        {n.body ? (
                          <>
                            <br />
                            <Text type="secondary">{n.body}</Text>
                          </>
                        ) : null}
                        <br />
                        <Text type="secondary" style={{ fontSize: 11 }}>
                          {n.created_at ? new Date(n.created_at).toLocaleString() : ""}
                        </Text>
                      </div>
                    </List.Item>
                  )}
                />
            </>
          )}

          {canSeeCrmNotifications && crm?.total ? (
            <>
          <Divider orientation="left">
          CRM Notifications
          <span style={{
            marginLeft: 8,
            background: "#1677ff",
            color: "#fff",
            padding: "2px 8px",
            borderRadius: 10,
            fontSize: 12
          }}>
            {crm?.total || 0}
          </span>
        </Divider>

          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

            <div style={{
              background: "#fff1f0",
              padding: 10,
              borderRadius: 8,
              borderLeft: "4px solid red"
            }}>
              🔴 <b>{crm.overdue}</b> Overdue Tasks
            </div>

            <div style={{
              background: "#fffbe6",
              padding: 10,
              borderRadius: 8,
              borderLeft: "4px solid orange"
            }}>
              🟡 <b>{crm.today}</b> Today Follow-ups
            </div>

            <div style={{
              background: "#f6ffed",
              padding: 10,
              borderRadius: 8,
              borderLeft: "4px solid green"
            }}>
              🟢 <b>{crm.deals}</b> Active Deals
            </div>

          </div>
            </>
          ) : null}

          {canUseWorkflows && recentWorkflowTasks.length > 0 && (
            <>
              {/* Workflow Tasks */}
              <Divider orientation="left">
                <BellOutlined /> Workflow Tasks
              </Divider>

              <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
                Info: Tasks from Assign to User, Assign to Role, and Assign Task actions appear here. For full history, open
                {" "}<Link to="/workflow-tasks" onClick={onClose}>My approvals</Link>.
              </Text>

              <List
                  dataSource={recentWorkflowTasks}
                  renderItem={(item) => (
                    <List.Item
                      key={item.id}
                      actions={[
                        item.action_type === "Assign Task"
                          ? (
                            <Button
                              key={`complete-${item.id}`}
                              type="link"
                              style={{ color: "#1677ff" }}
                              loading={actionLoading.complete === item.id}
                              onClick={() => openTaskForm(item)}
                            >
                              Complete Task
                            </Button>
                          )
                          : (
                            <Button
                              key={`approve-${item.id}`}
                              type="link"
                              style={{ color: "green" }}
                              loading={actionLoading.approve === item.id}
                              disabled={actionLoading.reject === item.id}
                              onClick={() => handleApprove(item.id)}
                            >
                              Approve
                            </Button>
                          ),

                        item.action_type === "Assign Task"
                          ? null
                          : (
                            <Button
                              key={`reject-${item.id}`}
                              type="link"
                              style={{ color: "red" }}
                              loading={actionLoading.reject === item.id}
                              disabled={actionLoading.approve === item.id}
                              onClick={() => handleReject(item.id)}
                            >
                              Reject
                            </Button>
                          ),
                      ]}
                    >
                      <div>
                        <Text strong>{item.workflow_name}</Text>
                        <br />

                        <Text type="secondary">
                          Record:{" "}
                          <b>
                            {item.record_type} #{item.record_id}
                          </b>
                        </Text>

                        {item.node_label && (
                          <>
                            <br />
                            <Text type="secondary">Step: <b>{item.node_label}</b></Text>
                          </>
                        )}

                        <br />

                        {getRecordEditPath(item.record_type, item.record_id) && (
                          <Link
                            to={getRecordEditPath(
                              item.record_type,
                              item.record_id
                            )}
                            style={{ fontSize: 12 }}
                          >
                            View Record
                          </Link>
                        )}
                      </div>
                    </List.Item>
                  )}
                />
            </>
          )}

          {/* Activities — always shown, even with nothing to list */}
          <Divider orientation="left">
          <div style={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
            <span>
              <BellOutlined /> Recent Activities
            </span>

            <Button
              type="link"
              size="small"
              onClick={() => {
                onClose(); // close drawer
                navigate("/settings/activities"); // adjust route if needed
              }}
            >
              View All
            </Button>
          </div>
        </Divider>

          {recentActivities.length > 0 ? (
            <List
              dataSource={recentActivities}
              renderItem={(item, i) => {
                const { icon: ActivityIcon, color, bg } = getActivityStyle(item.type);
                return (
                  <List.Item key={i} style={{ padding: "8px 10px" }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, width: "100%" }}>
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          width: 28,
                          height: 28,
                          borderRadius: "50%",
                          background: bg,
                          color,
                          flex: "none",
                        }}
                      >
                        <ActivityIcon style={{ fontSize: 14 }} />
                      </span>
                      <div style={{ minWidth: 0 }}>
                        <Text strong style={{ display: "block", lineHeight: 1.4 }}>
                          {item.activity_name}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {item.full_name ? `${item.full_name} · ` : ""}
                          {formatActivityTime(item.created_date)}
                        </Text>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />
          ) : activities.length === 0 ? (
            <Text
              type="secondary"
              style={{ display: "block", padding: "12px", marginLeft: 12 }}
            >
              No recent activities
            </Text>
          ) : null}

          {/* Attendance */}
          {recentAttendance.length > 0 && (
            <>
          <Divider orientation="left">
            <BellOutlined /> Attendance
          </Divider>

          <List
              dataSource={recentAttendance}
              renderItem={(item) => (
                <List.Item>
                  <div style={{ marginLeft: 12 }}>
                    {/* Employee name */}
                    <Text strong>{item.employee_name || "Employee"}</Text>

                    <br />

                    {/* Attendance date */}
                    <Text type="secondary">
                      Date:{" "}
                      {item.date
                        ? new Date(item.date).toLocaleDateString()
                        : "-"}
                    </Text>

                    <br />

                    {/* Attendance type */}
                    <Text>
                      Type:{" "}
                      <Text strong style={{ textTransform: "capitalize" }}>
                        {item.type}
                      </Text>
                    </Text>

                    <br />

                    {/* Attendance status */}
                    <Text>
                      Status:{" "}
                      <Text
                        strong
                        type={
                          item.status === "pending"
                            ? "warning"
                            : item.status === "approved"
                            ? "success"
                            : "secondary"
                        }
                        style={{ textTransform: "capitalize" }}
                      >
                        {item.status}
                      </Text>
                    </Text>
                  </div>
                </List.Item>
              )}
            />
            </>
          )}

          {canSeeCustomersSuppliers && (
            <>
          {/* New Customers */}
          {recentCustomers.length > 0 && (
            <>
              <Divider orientation="left">
                <UserAddOutlined /> New Customers
              </Divider>
              <List
                dataSource={recentCustomers}
                renderItem={(item, i) => (
                  <List.Item key={i}>
                    <Text>{item.acc_name}</Text>
                  </List.Item>
                )}
              />
            </>
          )}

          {/* New Suppliers */}
          {recentSuppliers.length > 0 && (
            <>
              <Divider orientation="left">
                <UserAddOutlined /> New Suppliers
              </Divider>
              <List
                dataSource={recentSuppliers}
                renderItem={(item, i) => (
                  <List.Item key={i}>
                    <Text>{item.acc_name}</Text>
                  </List.Item>
                )}
              />
            </>
          )}
            </>
          )}

        </div>
      )}

      <Modal
        open={!!taskForm}
        title={taskForm?.task_definition?.taskTitle || taskForm?.node_label || "Complete task"}
        okText="Save / Complete"
        okButtonProps={{ loading: actionLoading.complete === taskForm?.id }}
        onOk={submitTaskForm}
        onCancel={() => setTaskForm(null)}
        destroyOnClose
      >
        <Text type="secondary" style={{ display: "block", marginBottom: 8 }}>
          {taskForm?.task_definition?.taskDescription || "Complete the checklist and upload files if required."}
        </Text>

        {(taskForm?.task_definition?.checklist || []).length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <Text strong>Checklist</Text>
            <div style={{ marginTop: 8 }}>
              <Checkbox.Group value={taskChecklist} onChange={(vals) => setTaskChecklist(vals)}>
                <Space direction="vertical">
                  {(taskForm?.task_definition?.checklist || []).map((item, idx) => (
                    <Checkbox key={item.id || idx} value={item.id}>
                      {item.label || `Item ${idx + 1}`}{item.required ? " *" : ""}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </div>
          </div>
        )}

        {taskForm?.task_definition?.allowFileUpload && (
          <div>
            <Text strong>Files</Text>
            <Dragger
              multiple
              beforeUpload={() => false}
              fileList={taskFileList}
              onChange={({ fileList }) => setTaskFileList(fileList)}
              openFileDialogOnClick
              style={{ marginTop: 8 }}
            >
              <p style={{ marginBottom: 6 }}><b>Click to select files</b> or drag and drop here</p>
              <p style={{ margin: 0, color: "#666" }}>Multiple files are supported.</p>
            </Dragger>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {taskForm?.task_definition?.fileUploadRequired ? "At least one file is required for completion." : "File upload is optional."}
            </Text>
          </div>
        )}
      </Modal>
    </Drawer>
  );
};

export default RightSidebar;
